import {
  Component,
  DestroyRef,
  LOCALE_ID,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePipe, formatDate } from '@angular/common';
import { SvgComponent } from '@components/svg.component';
import { SummaryCourseCardComponent } from '@components/summary-course-card/summary-course-card.component';
import { HistoryService } from '@services/history.service';
import { OrderModel, OrderPaymentStatus } from '@models/order/order.model';
import { OrderItemModel } from '@models/order/order-item.model';
import { CourseModel } from '@models/course/course.model';
import { SessionOrderModel } from '@models/session-order/session-order.model';
import { SessionOrderService } from '@services/session-order.service';
import { ServiceImageService } from '@services/service-image.service';
import { ServiceImageModel } from '@models/service-image/service-image.model';
import { FormatPricePipe } from '@pipes/format-price.pipe';

@Component({
  selector: 'app-order-detail-page',
  imports: [TranslateModule, SvgComponent, SummaryCourseCardComponent, DatePipe, FormatPricePipe],
  templateUrl: './order-detail-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class OrderDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly historyService = inject(HistoryService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly serviceImageService = inject(ServiceImageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LOCALE_ID);

  protected readonly sessionOrder = signal<SessionOrderModel | null>(null);
  protected readonly serviceImages = signal<Map<number, ServiceImageModel>>(new Map());

  protected readonly participantGroups = computed(() => {
    const order = this.sessionOrder()?.order;
    const items = order?.order_items ?? [];
    const guestNumbers = [
      ...new Set(items.filter((item) => item.invite_number).map((item) => item.invite_number!)),
    ].sort((a, b) => a - b);

    const buildGroup = (guestNumber: number | null, groupItems: OrderItemModel[]) => {
      const extrasTotal = order
        ? groupItems.reduce((sum, item) => {
            if (!this.shouldShowPrice(order, item)) return sum;
            return sum + item.price * item.quantity;
          }, 0)
        : 0;
      const admissionTotal = guestNumber === null ? (order?.admission_price ?? 0) : 0;

      return {
        guestNumber,
        items: groupItems,
        totalPrice: extrasTotal + admissionTotal,
      };
    };

    return [
      buildGroup(
        null,
        items.filter((item) => !item.invite_number),
      ),
      ...guestNumbers.map((guestNumber) =>
        buildGroup(
          guestNumber,
          items.filter((item) => item.invite_number === guestNumber),
        ),
      ),
    ].filter((group) => group.items.length > 0);
  });

  protected readonly statusNote = computed(() => {
    const item = this.sessionOrder();
    if (!item) return null;

    const parts: string[] = [];

    if (item.order.cancelled_at) {
      const date = formatDate(item.order.cancelled_at, 'dd/MM/yyyy HH:mm', this.locale);
      parts.push(this.translate.instant('order_detail.cancelled_at', { date }));
    }
    if (item.order.cancellation_reason) {
      parts.push(
        this.translate.instant('order_detail.cancellation_reason', {
          reason: item.order.cancellation_reason,
        }),
      );
    }
    if (!item.order.cancelled_at) {
      if (item.forced_at) {
        const date = formatDate(item.forced_at, 'dd/MM/yyyy HH:mm', this.locale);
        parts.push(this.translate.instant('order_detail.forced_at', { date }));
      } else if (item.validated_at) {
        const date = formatDate(item.validated_at, 'dd/MM/yyyy HH:mm', this.locale);
        parts.push(this.translate.instant('order_detail.validated_at', { date }));
      }
      if (item.forced_comment) {
        parts.push(
          this.translate.instant('order_detail.forced_comment', { comment: item.forced_comment }),
        );
      }
    }

    if (parts.length === 0) return null;

    return {
      text: parts.join(' - '),
      isAlert: Boolean(item.forced_at) && !item.order.cancelled_at,
    };
  });

  constructor() {
    const sessionOrderId = Number(this.route.snapshot.paramMap.get('id'));

    this.sessionOrderService
      .getSessionOrder(sessionOrderId)
      .pipe(takeUntilDestroyed())
      .subscribe((sessionOrder) => {
        this.sessionOrder.set(sessionOrder);

        const serviceIds = [
          ...new Set(sessionOrder.order.order_items.map((item) => item.service.id)),
        ];
        this.serviceImageService
          .getServiceImages(serviceIds)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((images) => {
            this.serviceImages.set(new Map(images.map((img) => [img.id, img])));
          });
      });
  }

  goBack(): void {
    this.historyService.back();
  }

  protected getStatusClass(status: OrderPaymentStatus): string {
    const classes: Record<OrderPaymentStatus, string> = {
      valid: 'bg-vert-1',
      waiting: 'bg-orange-1',
      forced: 'bg-rose-1',
      cancelled: 'bg-gris-1',
    };
    return classes[status];
  }

  protected getPaymentMode(payments: Record<string, number> | null): string {
    if (!payments) return '—';
    return Object.keys(payments)[0] ?? '—';
  }

  protected shouldShowPrice(order: OrderModel, orderItem: OrderItemModel): boolean {
    return !(order.admission_price > 0 && !orderItem.is_extra);
  }

  protected isGuestItemFree(order: OrderModel, guestNumber: number | null): boolean {
    return guestNumber !== null && !order.is_invite_charged;
  }

  protected getBgColorFromOrderItem(orderItem: OrderItemModel): string | null {
    const image = this.serviceImages().get(orderItem.service.id);
    return image ? `#${image.bg}` : null;
  }

  protected getCourseFromOrderItem(orderItem: OrderItemModel): CourseModel {
    return {
      id: orderItem.plat_id ?? orderItem.id,
      name: orderItem.service_content,
      price: orderItem.price,
      nb: null,
      gpao_id: orderItem.plat_id_gpao,
      is_extra: orderItem.is_extra,
      is_meat_full_course: null,
      is_vegan_full_course: null,
      description: null,
      image: orderItem.service.image,
      labels: [],
      ingredients: [],
      calories: null,
      vat: orderItem.vat,
      bio: 0,
      aoc: 0,
      local: 0,
      local_detail: null,
      label_rouge: 0,
    };
  }
}
